package com.dotdrives.client

import com.dotdrives.core.uuidV7
import com.dotdrives.csv.CsvExistingGiver
import com.dotdrives.model.ActivityEvent
import com.dotdrives.model.Giver
import com.dotdrives.model.GiverSummary
import com.dotdrives.model.NewGiver
import com.dotdrives.model.Note
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.serialization.json.*
import kotlin.time.Instant
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Count
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

data class GiverPage(
    val items: List<GiverSummary>,
    val totalCount: Long,
)

class GiverClient(
    private val client: SupabaseClient = SupabaseProvider.client,
    private val authClient: AuthClient = AuthClient(client),
) {
    suspend fun list(
        orgId: String,
        query: String?,
        stewardUserId: String?,
        giverType: String?,
        tag: String?,
        page: Int,
        pageSize: Int,
        includeFinance: Boolean = false,
    ): GiverPage {
        require(page >= 1) { "page must be at least 1" }
        require(pageSize >= 1) { "pageSize must be at least 1" }
        val from = (page - 1L) * pageSize
        val to = from + pageSize - 1L
        val response = client.from(GIVERS_TABLE).select {
            count(Count.EXACT)
            range(from, to)
            order("display_name", Order.ASCENDING)
            order("id", Order.ASCENDING)
            filter {
                eq("org_id", orgId)
                query?.trim()?.takeIf(String::isNotEmpty)?.let {
                    ilike("display_name", "%$it%")
                }
                stewardUserId?.let { eq("steward_user_id", it) }
                giverType?.let { eq("giver_type", it) }
                tag?.let { contains("tags", listOf(it)) }
            }
        }
        val rows = response.decodeList<GiverSummaryRow>()
        val stewardNames = loadStewardNames(
            rows.mapNotNull(GiverSummaryRow::stewardUserId).distinct(),
        )
        val totals = if (includeFinance && rows.isNotEmpty()) rows.map { it.id }.chunked(100).flatMap { ids ->
            client.postgrest.rpc("giver_directory_totals", buildJsonObject { put("p_org", orgId); put("p_ids", JsonArray(ids.map(::JsonPrimitive))) }).decodeAs<List<GiverDirectoryTotals>>()
        }.associateBy { it.id } else emptyMap()
        return GiverPage(
            items = rows.map { row ->
                GiverSummary(
                    id = row.id,
                    displayName = row.displayName,
                    giverType = row.giverType,
                    stewardUserId = row.stewardUserId,
                    stewardName = row.stewardUserId?.let(stewardNames::get),
                    temperature = row.temperature,
                    tags = row.tags,
                    lifetime = totals[row.id]?.lifetime,
                    yearTotal = totals[row.id]?.yearTotal,
                    lastGiftAt = totals[row.id]?.lastGiftAt,
                    isActive = row.isActive,
                )
            },
            totalCount = response.countOrNull() ?: rows.size.toLong(),
        )
    }

    suspend fun get(orgId: String, id: String): Giver =
        client.from(GIVERS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", id)
                }
            }
            .decodeSingle()

    suspend fun create(orgId: String, giver: NewGiver): Giver =
        client.from(GIVERS_TABLE)
            .insert(
                giver.toInsert(orgId),
            ) {
                select()
            }
            .decodeSingle()

    suspend fun createMany(orgId: String, givers: List<NewGiver>): Int {
        if (givers.isEmpty()) return 0
        client.from(GIVERS_TABLE).insert(givers.map { it.toInsert(orgId) })
        return givers.size
    }

    suspend fun listForCsvImport(orgId: String): List<CsvExistingGiver> {
        return fetchAllPages(CSV_IMPORT_PAGE_SIZE.toLong()) { offset ->
            client.from(GIVERS_TABLE).select {
                range(offset, offset + CSV_IMPORT_PAGE_SIZE - 1)
                order("display_name", Order.ASCENDING)
                order("id", Order.ASCENDING)
                filter { eq("org_id", orgId) }
            }.decodeList<CsvExistingGiverRow>()
        }.map { CsvExistingGiver(it.id, it.displayName, it.email) }
    }

    suspend fun update(orgId: String, giver: Giver): Giver =
        client.from(GIVERS_TABLE)
            .update(
                GiverUpdate(
                    giverType = giver.giverType,
                    firstName = giver.firstName,
                    lastName = giver.lastName,
                    displayName = giver.displayName,
                    spouseName = giver.spouseName,
                    email = giver.email,
                    phone = giver.phone,
                    address = giver.address,
                    stewardUserId = giver.stewardUserId,
                    temperature = giver.temperature,
                    tags = giver.tags,
                ),
            ) {
                select()
                filter {
                    eq("org_id", orgId)
                    eq("id", giver.id)
                }
            }
            .decodeSingle()

    suspend fun timeline(
        orgId: String,
        giverId: String,
        verb: String?,
        limit: Int,
    ): List<ActivityEvent> =
        client.from(ACTIVITY_EVENTS_TABLE)
            .select {
                limit(limit.toLong())
                order("occurred_at", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("subject_type", GIVER_SUBJECT_TYPE)
                    eq("subject_id", giverId)
                    verb?.let { eq("verb", it) }
                }
            }
            .decodeList()

    suspend fun notes(orgId: String, giverId: String): List<Note> =
        client.from(NOTES_TABLE)
            .select {
                order("created_at", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("giver_id", giverId)
                }
            }
            .decodeList()

    suspend fun addNote(
        orgId: String,
        giverId: String,
        title: String,
        body: String,
    ): Note =
        client.from(NOTES_TABLE)
            .insert(
                NoteInsert(
                    id = uuidV7(),
                    orgId = orgId,
                    giverId = giverId,
                    title = title,
                    body = body,
                    createdBy = authClient.requireCurrentUserId(),
                ),
            ) {
                select()
            }
            .decodeSingle()

    private suspend fun loadStewardNames(userIds: List<String>): Map<String, String> {
        if (userIds.isEmpty()) return emptyMap()
        return client.from(PROFILES_TABLE)
            .select {
                filter {
                    isIn("user_id", userIds)
                }
            }
            .decodeList<StewardProfileRow>()
            .associate { profile ->
                profile.userId to listOf(profile.firstName, profile.lastName)
                    .filter(String::isNotBlank)
                    .joinToString(" ")
            }
    }
}

@Serializable
private data class GiverSummaryRow(
    val id: String,
    @SerialName("display_name")
    val displayName: String,
    @SerialName("giver_type")
    val giverType: String,
    @SerialName("steward_user_id")
    val stewardUserId: String?,
    val temperature: String,
    val tags: List<String>,
    @SerialName("legacy_is_active")
    val isActive: Boolean = true,
)

@Serializable
private data class CsvExistingGiverRow(
    val id: String,
    @SerialName("display_name")
    val displayName: String,
    val email: String?,
)

@Serializable
private data class StewardProfileRow(
    @SerialName("user_id")
    val userId: String,
    @SerialName("first_name")
    val firstName: String,
    @SerialName("last_name")
    val lastName: String,
)

@Serializable
private data class GiverInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("giver_type")
    val giverType: String,
    @SerialName("first_name")
    val firstName: String?,
    @SerialName("last_name")
    val lastName: String?,
    @SerialName("display_name")
    val displayName: String,
    @SerialName("spouse_name")
    val spouseName: String?,
    val email: String?,
    val phone: String?,
    val address: String?,
    @SerialName("steward_user_id")
    val stewardUserId: String?,
    val tags: List<String>,
)

private fun NewGiver.toInsert(orgId: String): GiverInsert =
    GiverInsert(
        id = uuidV7(),
        orgId = orgId,
        giverType = giverType,
        firstName = firstName.takeIf(String::isNotEmpty),
        lastName = lastName.takeIf(String::isNotEmpty),
        displayName = displayName,
        spouseName = spouseName,
        email = email,
        phone = phone,
        address = address,
        stewardUserId = stewardUserId,
        tags = tags,
    )

@Serializable
private data class GiverUpdate(
    @SerialName("giver_type")
    val giverType: String,
    @SerialName("first_name")
    val firstName: String?,
    @SerialName("last_name")
    val lastName: String?,
    @SerialName("display_name")
    val displayName: String,
    @SerialName("spouse_name")
    val spouseName: String?,
    val email: String?,
    val phone: String?,
    val address: String?,
    @SerialName("steward_user_id")
    val stewardUserId: String?,
    val temperature: String,
    val tags: List<String>,
)

@Serializable
private data class NoteInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("giver_id")
    val giverId: String,
    val title: String,
    val body: String,
    @SerialName("created_by")
    val createdBy: String,
)

private const val GIVERS_TABLE = "givers"
private const val NOTES_TABLE = "notes"
private const val ACTIVITY_EVENTS_TABLE = "activity_events"
private const val PROFILES_TABLE = "profiles"
private const val GIVER_SUBJECT_TYPE = "giver"
private const val CSV_IMPORT_PAGE_SIZE = 1_000

@Serializable private data class GiverDirectoryTotals(val id:String,val lifetime:Double,@SerialName("year_total")val yearTotal:Double,@SerialName("last_gift_at")val lastGiftAt:Instant?=null)
